import { useEffect } from "react";

// Simple status modal rendered while an export is assembling. The
// parent drives state updates by mutating a `step` value; this
// component just displays it. Steps are free-form strings so new
// phases can be added without touching this file.
//
// Also surfaces the running AI-call count — regenerating a stale
// summary or story costs one call each, and the user has a
// monthly quota they may want to watch.

export interface ExportProgressState {
  label: string; // e.g. "Generating Case Brief..."
  // Ordered step list — the one matching `currentStep` is shown
  // bold with a spinner, earlier steps are ✓, later are greyed.
  steps: string[];
  currentStep: number; // 0-based index into `steps`
  // Number of fresh AI calls consumed to produce this export.
  // Displayed inline so the user can tally usage at a glance.
  aiCallsUsed: number;
  // When true, an AI call is in flight right now — the step's
  // label gets an "(1 AI call)" note.
  aiCallInProgress?: boolean;
  // When set, a blocking error is shown with a retry/close footer.
  error?: string | null;
}

interface Props {
  state: ExportProgressState | null;
  onClose: () => void;
}

export default function ExportProgressModal({ state, onClose }: Props) {
  useEffect(() => {
    if (!state?.error) return;
    function onKey(e: KeyboardEvent) {
      if (e.key === "Escape") onClose();
    }
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [state?.error, onClose]);

  if (!state) return null;
  return (
    <div className="modal-backdrop" role="presentation">
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="export-progress-title"
        className="export-progress-modal"
      >
        <h2 id="export-progress-title" className="modal-title">
          {state.label}
        </h2>
        <ol className="export-progress-steps">
          {state.steps.map((s, i) => {
            const done = i < state.currentStep;
            const active = i === state.currentStep;
            return (
              <li
                key={i}
                className={`export-progress-step${
                  done ? " done" : active ? " active" : " pending"
                }`}
              >
                <span className="export-progress-step-marker" aria-hidden>
                  {done ? "✓" : active ? "●" : "○"}
                </span>
                <span>
                  {s}
                  {active && state.aiCallInProgress && (
                    <span className="export-progress-ai-note">
                      {" "}
                      (1 AI call)
                    </span>
                  )}
                </span>
              </li>
            );
          })}
        </ol>
        {state.aiCallsUsed > 0 && (
          <div className="export-progress-ai-total">
            {state.aiCallsUsed} AI call
            {state.aiCallsUsed === 1 ? "" : "s"} used so far
          </div>
        )}
        {state.error && (
          <>
            <div className="form-error">{state.error}</div>
            <div className="export-progress-footer">
              <button type="button" onClick={onClose}>
                Close
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
